import { Injectable } from "@angular/core";
import { ProcessingErrorCode } from "../interfaces/email-settings.interface";

@Injectable({
  providedIn: "root",
})
export class ProcessingErrorCodesHelper {
  public getTextFromProcessingCode(
    processingErrorCode: ProcessingErrorCode,
  ): string {
    switch (processingErrorCode) {
      case ProcessingErrorCode.SystemError:
        return "System Error";
      case ProcessingErrorCode.EmptyScheduledOrder:
        return "Empty Scheduled Order";
      case ProcessingErrorCode.NoItemsToShip:
        return "No Items To Ship";
      case ProcessingErrorCode.ShippingRateNotFound:
        return "Shipping Rate Not Found";
      case ProcessingErrorCode.PaymentIntegrationNotFound:
        return "Payment Integration Not Found";
      case ProcessingErrorCode.NoPaymentMethod:
        return "No Payment Method";
      case ProcessingErrorCode.PaymentFailed:
        return "Payment Failed";
      case ProcessingErrorCode.PaymentFailedGatewayCommunication:
        return "Payment Failed Gateway Communication";
      case ProcessingErrorCode.ClientOrderCreationFailure:
        return "Client Order Creation Failure";
      case ProcessingErrorCode.ClientOrderUpdateFailure:
        return "Client Order Update Failure";
      case ProcessingErrorCode.ClientOrderInvalid:
        return "Client Order Invalid";
      default:
        return "Unknown Error";
    }
  }

  public getEnumStringFromProcessingCode(
    processingErrorCode: ProcessingErrorCode,
  ): string {
    switch (processingErrorCode) {
      case ProcessingErrorCode.SystemError:
        return "SystemError";
      case ProcessingErrorCode.EmptyScheduledOrder:
        return "EmptyScheduledOrder";
      case ProcessingErrorCode.NoItemsToShip:
        return "NoItemsToShip";
      case ProcessingErrorCode.ShippingRateNotFound:
        return "ShippingRateNotFound";
      case ProcessingErrorCode.PaymentIntegrationNotFound:
        return "PaymentIntegrationNotFound";
      case ProcessingErrorCode.NoPaymentMethod:
        return "PaymentMethodNull";
      case ProcessingErrorCode.PaymentFailed:
        return "PaymentFailed";
      case ProcessingErrorCode.PaymentFailedGatewayCommunication:
        return "PaymentFailedGatewayCommunication";
      case ProcessingErrorCode.ClientOrderCreationFailure:
        return "ClientOrderCreationFailure";
      case ProcessingErrorCode.ClientOrderUpdateFailure:
        return "ClientOrderUpdateFailure";
      case ProcessingErrorCode.ClientOrderInvalid:
        return "ClientOrderCreationInvalidResponse";
      default:
        return "Unknown";
    }
  }

  public getProcessingCodeFromEnumString(enumString: string): number | null {
    switch (enumString) {
      case "SystemError":
        return ProcessingErrorCode.SystemError;
      case "EmptyScheduledOrder":
        return ProcessingErrorCode.EmptyScheduledOrder;
      case "NoItemsToShip":
        return ProcessingErrorCode.NoItemsToShip;
      case "ShippingRateNotFound":
        return ProcessingErrorCode.ShippingRateNotFound;
      case "PaymentIntegrationNotFound":
        return ProcessingErrorCode.PaymentIntegrationNotFound;
      case "PaymentMethodNull":
        return ProcessingErrorCode.NoPaymentMethod;
      case "PaymentFailed":
        return ProcessingErrorCode.PaymentFailed;
      case "PaymentFailedGatewayCommunication":
        return ProcessingErrorCode.PaymentFailedGatewayCommunication;
      case "ClientOrderCreationFailure":
        return ProcessingErrorCode.ClientOrderCreationFailure;
      case "ClientOrderUpdateFailure":
        return ProcessingErrorCode.ClientOrderUpdateFailure;
      case "ClientOrderCreationInvalidResponse":
        return ProcessingErrorCode.ClientOrderInvalid;
      default:
        return null;
    }
  }

  public getDocumentationUrlFromProcessingErrorCode(
    processingErrorCode: ProcessingErrorCode,
  ): string | null {
    switch (processingErrorCode) {
      case ProcessingErrorCode.SystemError:
        return "https://docs.qpilot.cloud/docs/99-unknown-error";
      case ProcessingErrorCode.EmptyScheduledOrder:
        return "https://docs.qpilot.cloud/docs/1000-empty-scheduled-order";
      case ProcessingErrorCode.NoItemsToShip:
        return "https://docs.qpilot.cloud/docs/1001-no-items-to-ship";
      case ProcessingErrorCode.ShippingRateNotFound:
        return "https://docs.qpilot.cloud/docs/1002-shipping-rate-not-found";
      case ProcessingErrorCode.PaymentIntegrationNotFound:
        return "https://docs.qpilot.cloud/docs/1003-payment-integration-not-found";
      case ProcessingErrorCode.NoPaymentMethod:
        return "https://docs.qpilot.cloud/docs/1004-no-payment-method";
      case ProcessingErrorCode.PaymentFailed:
        return "https://docs.qpilot.cloud/docs/2000-payment-failed";
      case ProcessingErrorCode.PaymentFailedGatewayCommunication:
        return "https://docs.qpilot.cloud/docs/2000-payment-failed";
      case ProcessingErrorCode.ClientOrderCreationFailure:
        return "https://docs.qpilot.cloud/docs/3000-client-order-creation-failure";
      case ProcessingErrorCode.ClientOrderUpdateFailure:
        return "https://docs.qpilot.cloud/docs/3001-client-order-update-failure";
      case ProcessingErrorCode.ClientOrderInvalid:
        return "https://docs.qpilot.cloud/docs/3002-client-order-creation-invalid-response";
      default:
        return null;
    }
  }

  public getEmailPreviewFromProcessingCode(
    processingErrorCode: ProcessingErrorCode,
  ): string | null {
    switch (processingErrorCode) {
      case ProcessingErrorCode.EmptyScheduledOrder:
        return "https://qpilotassetsstorage.blob.core.windows.net/assets/tutorials/empty-soo.png";
      case ProcessingErrorCode.NoItemsToShip:
        return "https://qpilotassetsstorage.blob.core.windows.net/assets/tutorials/no-items.png";
      case ProcessingErrorCode.NoPaymentMethod:
        return "https://qpilotassetsstorage.blob.core.windows.net/assets/tutorials/no-method.png";
      case ProcessingErrorCode.PaymentFailed:
        return "https://qpilotassetsstorage.blob.core.windows.net/assets/tutorials/payment-error.png";
      default:
        return null;
    }
  }

  public getProcessingErrorCodeOptions(): Record<string, string> {
    return {
      1000: "Empty Scheduled Order (1000)",
      1001: "No Items To Ship (1001)",
      1002: "Shipping Rate Not Found (1002)",
      1003: "Payment Integration Not Found (1003)",
      1004: "No Payment Method (1004)",
      2000: "Payment Failed (2000)",
      2001: "Payment Gateway Communication Failure (2001)",
      3000: "Client Order Creation Failure (3000)",
      3001: "Client Order Update Failure (3001)",
      3002: "Client Order Invalid (3002)",
      99: "System Error (99)",
    };
  }

  public getAvailableErrorCodes(): ProcessingErrorCode[] {
    return [
      ProcessingErrorCode.EmptyScheduledOrder,
      ProcessingErrorCode.NoItemsToShip,
      ProcessingErrorCode.NoPaymentMethod,
      ProcessingErrorCode.PaymentFailed,
    ];
  }

  public getUpcomingValidationErrorCodes(): ProcessingErrorCode[] {
    return [
      ProcessingErrorCode.EmptyScheduledOrder,
      ProcessingErrorCode.NoItemsToShip,
      ProcessingErrorCode.NoPaymentMethod,
    ];
  }
}
